import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  ServerIcon,
  CpuChipIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Sparkline from './Sparkline';
import {
  MACHINE_ROLES,
  STATUS_COLORS,
  formatBytes,
  formatUptime,
  getGpuTempColor,
} from '../constants';

/**
 * Machine card component for displaying server health and metrics.
 */
export default function MachineCard({
  machine,
  metrics,
  onExpand,
  onRestartContainer,
  expanded = false,
}) {
  const cardRef = useRef(null);
  const barsRef = useRef([]);

  // Animate resource bars
  useEffect(() => {
    if (metrics && barsRef.current.length > 0) {
      const cpuPercent = metrics.cpu?.percent || 0;
      const memPercent = metrics.memory?.percent || 0;

      barsRef.current.forEach((el, idx) => {
        if (!el) return;
        const percent = [cpuPercent, memPercent][idx] || 0;
        gsap.to(el, {
          width: `${percent}%`,
          duration: 0.8,
          ease: 'power2.out',
        });
      });
    }
  }, [metrics]);

  const isOnline = machine.is_online;
  const role = MACHINE_ROLES[machine.role] || { label: machine.role, color: '#8fbba8' };
  const cpuPercent = metrics?.cpu?.percent || 0;
  const memPercent = metrics?.memory?.percent || 0;

  // Get health status
  const getHealthStatus = () => {
    if (!isOnline) return { status: 'offline', color: STATUS_COLORS.offline };
    if (cpuPercent > 90 || memPercent > 90) return { status: 'critical', color: STATUS_COLORS.critical };
    if (cpuPercent > 70 || memPercent > 70) return { status: 'warning', color: STATUS_COLORS.warning };
    return { status: 'healthy', color: STATUS_COLORS.healthy };
  };

  const health = getHealthStatus();

  // GPU summary (if available)
  const gpus = metrics?.gpus || [];
  const hasGpu = gpus.length > 0;

  // Container summary
  const containers = metrics?.containers || [];
  const runningContainers = containers.filter(c => c.status === 'running').length;
  const totalContainers = containers.length;

  return (
    <div
      className={`machine-card ${expanded ? 'expanded' : ''}`}
      ref={cardRef}
      onClick={() => onExpand && onExpand(machine.hostname)}
    >
      {/* Header */}
      <div className="machine-card-header">
        <div className="machine-icon">
          {hasGpu ? <CpuChipIcon className="icon" /> : <ServerIcon className="icon" />}
        </div>
        <div className="machine-info">
          <div className="machine-name">{machine.display_name}</div>
          <div className="machine-hostname">{machine.hostname}</div>
        </div>
        <div
          className="machine-status"
          style={{ '--status-color': health.color }}
          title={health.status}
        >
          <span className="status-dot" />
          <span className="status-label">{isOnline ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {/* Role badge */}
      <div className="machine-role" style={{ '--role-color': role.color }}>
        {role.label}
      </div>

      {/* Resource bars */}
      {isOnline && metrics && (
        <div className="machine-resources">
          <div className="resource-row">
            <span className="resource-label">CPU</span>
            <div className="resource-bar-container">
              <div
                className="resource-bar-fill"
                ref={(el) => (barsRef.current[0] = el)}
                style={{
                  width: '0%',
                  backgroundColor: cpuPercent > 90 ? STATUS_COLORS.critical : cpuPercent > 70 ? STATUS_COLORS.warning : STATUS_COLORS.healthy,
                }}
              />
            </div>
            <span className="resource-value">{cpuPercent.toFixed(0)}%</span>
          </div>
          <div className="resource-row">
            <span className="resource-label">MEM</span>
            <div className="resource-bar-container">
              <div
                className="resource-bar-fill"
                ref={(el) => (barsRef.current[1] = el)}
                style={{
                  width: '0%',
                  backgroundColor: memPercent > 90 ? STATUS_COLORS.critical : memPercent > 70 ? STATUS_COLORS.warning : STATUS_COLORS.healthy,
                }}
              />
            </div>
            <span className="resource-value">{memPercent.toFixed(0)}%</span>
          </div>
        </div>
      )}

      {/* Container count */}
      {isOnline && totalContainers > 0 && (
        <div className="machine-containers">
          <span className="container-count">
            {runningContainers}/{totalContainers} containers
          </span>
        </div>
      )}

      {/* GPU summary (compact) */}
      {isOnline && hasGpu && (
        <div className="machine-gpu-summary">
          {gpus.map((gpu, idx) => (
            <div key={idx} className="gpu-mini">
              <span className="gpu-label">GPU {idx}</span>
              <span className="gpu-util">{gpu.utilization_percent}%</span>
              <span
                className="gpu-temp"
                style={{ color: getGpuTempColor(gpu.temperature_c) }}
              >
                {gpu.temperature_c}C
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sparkline placeholder */}
      {isOnline && metrics?.cpu_history && (
        <div className="machine-sparkline">
          <Sparkline
            data={metrics.cpu_history}
            color={STATUS_COLORS.healthy}
            width={120}
            height={20}
          />
        </div>
      )}

      {/* Offline message */}
      {!isOnline && (
        <div className="machine-offline">
          <span>Last seen: {machine.last_seen ? new Date(machine.last_seen).toLocaleString() : 'Never'}</span>
        </div>
      )}
    </div>
  );
}
